import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/contexts/LanguageContext";

export function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Streamlined navigation — fewer items, more premium feel.
  // "Professionnels" lives in the footer only. "Accueil" is the logo itself.
  // Top-right "Prendre rendez-vous" CTA removed.
  const links = [
    { to: "/collections", label: t.nav.collections },
    { to: "/projets", label: t.nav.projects },
    { to: "/materiaux", label: t.nav.materials },
    { to: "/savoir-faire", label: t.nav.savoir },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,padding] duration-500 ease-arven ${
        scrolled
          ? "bg-[rgba(17,17,17,0.85)] backdrop-blur-md border-b border-white/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-14 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link">
          <Logo variant="default" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative text-[12px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                  isActive
                    ? "text-arven-gold"
                    : "text-arven-offwhite/85 hover:text-arven-offwhite"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-block">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 right-0 mx-auto h-px w-6 bg-arven-gold" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden md:flex items-center gap-1 text-[11px] tracking-[0.2em] font-medium"
            data-testid="language-switcher"
          >
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-2 py-1 transition-colors duration-300 ${
                lang === "fr" ? "text-arven-gold" : "text-arven-offwhite/55 hover:text-arven-offwhite"
              }`}
            >
              FR
            </button>
            <span className="text-arven-offwhite/30">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-2 py-1 transition-colors duration-300 ${
                lang === "en" ? "text-arven-gold" : "text-arven-offwhite/55 hover:text-arven-offwhite"
              }`}
            >
              EN
            </button>
          </div>

          <button
            className="lg:hidden p-2 text-arven-offwhite"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden absolute top-full left-0 right-0 bg-arven-noir border-t border-white/5 px-6 py-8"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] py-1 ${
                    isActive ? "text-arven-gold" : "text-arven-offwhite/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/professionnels"
              className="text-sm uppercase tracking-[0.2em] py-1 text-arven-offwhite/60"
            >
              {t.nav.pros}
            </Link>
            <div className="mt-3 flex items-center gap-2 text-[11px] tracking-[0.2em]">
              <button
                onClick={() => setLang("fr")}
                className={`px-2 py-1 ${lang === "fr" ? "text-arven-gold" : "text-arven-offwhite/55"}`}
              >FR</button>
              <span className="text-arven-offwhite/30">/</span>
              <button
                onClick={() => setLang("en")}
                className={`px-2 py-1 ${lang === "en" ? "text-arven-gold" : "text-arven-offwhite/55"}`}
              >EN</button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
